import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { BusinessCard } from "@/components/Cards";
import { HERO_MURAL, SECTION_MAP } from "@/lib/constants";

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const section = params.get("section") || "";
  const urlCategory = params.get("category") || "";
  const sectionCfg = SECTION_MAP[section];

  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/businesses/categories").then((r) => setCategories(["All", ...r.data])); }, []);

  // Sync category from URL / section
  useEffect(() => {
    if (sectionCfg?.type === "category") setCat(sectionCfg.value);
    else if (urlCategory) setCat(urlCategory);
    else if (!section) setCat("All");
  }, [section, urlCategory]); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (cat !== "All" && !(sectionCfg?.type === "tags")) query.category = cat;
    if (q) query.q = q;
    const t = setTimeout(() => {
      api.get("/businesses", { params: query }).then((r) => setBusinesses(r.data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, q, section]); // eslint-disable-line

  const displayed = useMemo(() => {
    if (sectionCfg?.type === "tags") {
      const want = sectionCfg.value;
      return businesses.filter((b) => (b.tags || []).some((t) => want.includes(t)));
    }
    return businesses;
  }, [businesses, section]); // eslint-disable-line

  const title = sectionCfg?.title || "Explore RiNo";

  const pickCat = (c) => {
    setCat(c);
    setParams(c === "All" ? {} : { category: c });
  };

  return (
    <div>
      <Seo title={`${title} — Business Directory`} description="Explore RiNo — the most complete directory of businesses in Denver's River North Art District: food & drink, nightlife, shopping, art & culture and more." image={HERO_MURAL} />
      <PageHeader overline="The Directory" title={title} subtitle="The most complete guide to every brewery, gallery, restaurant, shop, and studio in the River North Art District." image={HERO_MURAL} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-32 space-y-6">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search businesses..."
                className="w-full bg-[#141414] border border-white/15 pl-9 pr-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]" />
            </div>
            <div>
              <h3 className="font-accent text-[#00E5FF] text-sm mb-3">Categories</h3>
              <div className="flex flex-col gap-1">
                {categories.map((c) => (
                  <button key={c} data-testid={`filter-${c.replace(/\s|\+/g, "-").toLowerCase()}`} onClick={() => pickCat(c)}
                    className={`text-left px-3 py-2 text-sm border transition-colors ${cat === c && !sectionCfg?.type ? "border-[#FF0055] bg-[#FF0055]/10 text-white" : (sectionCfg?.type === "category" && sectionCfg.value === c) ? "border-[#FF0055] bg-[#FF0055]/10 text-white" : "border-transparent text-neutral-400 hover:text-white hover:border-white/10"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="flex items-center justify-between mb-6">
            <span className="text-neutral-400 text-sm" data-testid="directory-count">{displayed.length} {displayed.length === 1 ? "listing" : "listings"}</span>
          </div>
          {loading ? (
            <div className="text-neutral-500 py-20 text-center">Loading RiNo...</div>
          ) : displayed.length === 0 ? (
            <div className="text-neutral-500 py-20 text-center">No businesses match your search.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {displayed.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
